import React from "react";
import {
  Button,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@material-ui/core";
import { StarRating } from "./ReviewTable";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3, 3, 2.5),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  summaryBlock: {
    paddingLeft: theme.spacing(1),
  },
  averageRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(0.5),
  },
  averageValue: {
    margin: 0,
    minWidth: 48,
  },
  averageMeta: {
    paddingLeft: theme.spacing(0.5),
  },
  filterControl: {
    minWidth: 200,
    marginRight: theme.spacing(0.5),
  },
  ratingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0.5, 0),
  },
  ratingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: theme.spacing(2, 2.5),
    textAlign: "left",
    textTransform: "none",
    minHeight: 118,
    width: "100%",
    borderRadius: theme.shape.borderRadius,
  },
  ratingBoxActive: {
    boxShadow: theme.shadows[2],
  },
  ratingBoxLabel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    paddingLeft: theme.spacing(0.5),
  },
  stars: {
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(0.25),
  },
  ratingCount: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    paddingLeft: theme.spacing(0.25),
  },
  ratingText: {
    paddingLeft: theme.spacing(0.25),
  },
}));

const ReviewRatingSummary = ({
  summary,
  selectedRating,
  onRatingChange,
}) => {
  const classes = useStyles();
  const byRating = summary?.by_rating || {};
  const total = summary?.total || 0;

  return (
    <Paper className={classes.root} elevation={1}>
      <div className={classes.header}>
        <div className={classes.summaryBlock}>
          <Typography variant="subtitle1" gutterBottom>
            Review summary
          </Typography>
          <div className={classes.averageRow}>
            <Typography variant="h4" className={classes.averageValue}>
              {summary?.average > 0 ? summary.average.toFixed(1) : "0.0"}
            </Typography>
            <div className={classes.averageMeta}>
              <StarRating rating={Math.round(summary?.average || 0)} />
              <Typography variant="caption" display="block" color="textSecondary">
                {total} {total === 1 ? "review" : "reviews"} total
              </Typography>
            </div>
          </div>
        </div>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterControl}
        >
          <InputLabel id="review-rating-filter-label">Filter by rating</InputLabel>
          <Select
            labelId="review-rating-filter-label"
            value={selectedRating}
            onChange={(event) => onRatingChange(event.target.value)}
            label="Filter by rating"
          >
            <MenuItem value="">All ratings</MenuItem>
            {[5, 4, 3, 2, 1].map((rating) => (
              <MenuItem key={rating} value={String(rating)}>
                {rating} {rating === 1 ? "star" : "stars"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={classes.ratingGrid}>
        {[5, 4, 3, 2, 1].map((rating) => {
          const count = byRating[rating] || 0;
          const isActive = selectedRating === String(rating);

          return (
            <Button
              key={rating}
              variant={isActive ? "contained" : "outlined"}
              color={isActive ? "primary" : "default"}
              onClick={() => onRatingChange(isActive ? "" : String(rating))}
              className={`${classes.ratingBox} ${isActive ? classes.ratingBoxActive : ""}`}
              classes={{ label: classes.ratingBoxLabel }}
            >
              <div className={classes.stars}>
                <StarRating rating={rating} />
              </div>
              <Typography variant="body1" className={classes.ratingCount}>
                {count}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.ratingText}
              >
                {rating} {rating === 1 ? "star" : "stars"}
              </Typography>
            </Button>
          );
        })}
      </div>
    </Paper>
  );
};

export default ReviewRatingSummary;
